import { useTheme } from '@mui/material/styles';
import React, { CSSProperties } from 'react';
import { makeStyles } from 'tss-react/mui';
import { SkodaSliderProps } from '../skodaSlider';

const useStyles = makeStyles({ name: 'RangeSliderWrapper' })(theme => ({
	panelValues: {
		display: 'flex',
		marginLeft: 20,
		marginRight: 20,
		marginBottom: 12,
		transition: 'opacity 1s',
		'&.disabled': {
			opacity: 0.5
		}
	},
	valueFragment: {
		textAlign: 'center',
		fontWeight: 'bold',
		display: 'flex',
		justifyContent: 'center'
	},
	bubble: {
		fontSize: 12,
		lineHeight: '16px',
		padding: '8px 12px',
		background: theme.palette.background.paper,
		borderColor: theme.palette.background.paper,
		borderRadius: 2,
		position: 'relative',
		'&:after': {
			content: '""',
			position: 'absolute',
			top: '100%',
			left: '50%',
			transform: 'translateX(-50%)',
			borderTop: '10px solid',
			borderTopColor: 'inherit',
			borderLeft: '9px solid transparent',
			borderRight: '9px solid transparent'
		}
	},
	legendPanel: {
		display: 'flex',
		flexWrap: 'wrap',
		margin: '-6px -12px',
		marginTop: 4,
		transition: 'opacity 1s',
		'&.disabled': {
			opacity: 0.5
		},
		[theme.breakpoints.up('md')]: {
			margin: '-8px -12px',
			marginTop: 18
		},
		[theme.breakpoints.up('lg')]: {
			margin: '-8px -8px',
			marginTop: 22
		},
		[theme.breakpoints.up('xl')]: {
			margin: '-8px -12px',
			marginTop: 22
		}
	},
	legendItem: {
		display: 'flex',
		alignItems: 'center',
		margin: '6px 12px',
		[theme.breakpoints.up('md')]: {
			margin: '8px 12px'
		},
		[theme.breakpoints.up('lg')]: {
			margin: '8px 8px'
		},
		[theme.breakpoints.up('xl')]: {
			margin: '8px 12px'
		}
	},
	colorIndicator: {
		width: 12,
		height: 12,
		borderRadius: 2,
		marginRight: 10
	},
	legendText: {
		fontSize: 12,
		lineHeight: '16px',
		[theme.breakpoints.up('lg')]: {
			fontSize: 14,
			lineHeight: '20px'
		}
	}
}));

export type SkodaSliderWrapperProps = {
	children: React.ReactElement<SkodaSliderProps>;
	showDiffs?: boolean;
	formatDiffs?: (val: number) => string;
	legend?: string[];
	style?: CSSProperties;
};

export const SkodaSliderWrapper = (props: SkodaSliderWrapperProps) => {
	const { children, showDiffs, formatDiffs, legend, ...other } = props;
	const { min, max, value, colors, disabled } = props.children.props;

	return (
		<div {...other}>
			{showDiffs &&
				<PercentagePanel
					min={min}
					max={max}
					value={value}
					formatDiffs={formatDiffs}
					disabled={disabled} />}
			{children}
			{legend && legend.length &&
				<Legend
					names={legend}
					colors={colors}
					disabled={disabled} />}
		</div>
	);
};

type PercentagePanelProps = {
	min: number;
	max: number;
	value: number | number[];
	formatDiffs?: (val: number) => string;
	disabled?: boolean;
};

const PercentagePanel: React.FC<PercentagePanelProps> = (props) => {
	const { min, max, value, formatDiffs, disabled } = props;

	let { classes: styles, cx } = useStyles();

	const points = Array.isArray(value) ? [min, ...value, max] : [min, value, max];
	let ranges: { from: number; to: number }[] = [];
	for (let i = 0; i < points.length - 1; i++) {
		ranges = [...ranges, ({ from: points[i], to: points[i + 1] })];
	}

	const diffs = ranges.map(range => range.to - range.from);
	const sliderRange = max - min;
	const percs = diffs.map(diff => diff / sliderRange);

	return (
		<div data-test='percentages' className={cx(styles.panelValues, disabled && 'disabled')}>
			{diffs.map((diff, index) =>
				<div
					key={index}
					className={styles.valueFragment}
					style={{ width: percs[index] * 100 + '%' }}>
					<div data-test='percentage-bubble' className={styles.bubble}>
						{formatDiffs ? formatDiffs(diff) : diff}
					</div>
				</div>
			)}
		</div>
	);
};

type LegendProps = {
	names: string[];
	colors?: string[];
	disabled?: boolean;
};

const Legend: React.FC<LegendProps> = (props) => {
	const { colors, names, disabled } = props;
	const theme = useTheme();

	let { classes: styles, cx } = useStyles();

	const legendColors = colors ?? theme.Skoda.settings.skodaSlider.colors;

	return (
		<div className={cx(styles.legendPanel, disabled && 'disabled')}>
			{names.map((name, index) =>
				<div key={index} className={styles.legendItem}>
					<div
						className={cx(styles.colorIndicator, disabled && 'disabled')}
						style={{ background: legendColors[index] }} />
					<span className={styles.legendText}>{name}</span>
				</div>)}
		</div>
	);
};
